<template>
  <div class="image-cropper-modal">
    <b-modal
      v-model="showModal"
      title="Crop Image"
      size="lg"
      @hidden="onModalHidden"
      @ok="cropImage"
      ok-title="Crop & Upload"
      cancel-title="Cancel"
    >
      <div class="cropper-container">
        <div class="cropper-wrapper">
          <div class="canvas-container" ref="canvasContainer">
            <canvas
              ref="canvas"
              @mousedown="startDrag"
              @mousemove="drag"
              @mouseup="endDrag"
              @wheel="handleWheel"
              class="crop-canvas"
            ></canvas>
          </div>
        </div>

        <div class="cropper-controls mt-3">
          <div class="d-flex justify-content-between align-items-center">
            <div class="zoom-controls">
              <b-button-group size="sm">
                <b-button variant="outline-secondary" @click="zoomIn">
                  <i class="fas fa-search-plus"></i> Zoom In
                </b-button>
                <b-button variant="outline-secondary" @click="zoomOut">
                  <i class="fas fa-search-minus"></i> Zoom Out
                </b-button>
                <b-button variant="outline-secondary" @click="resetZoom">
                  <i class="fas fa-undo"></i> Reset
                </b-button>
              </b-button-group>
            </div>

            <div class="crop-info">
              <small class="text-muted">
                Drag to move • Scroll to zoom • Image always covers the entire
                crop area
              </small>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <div class="d-flex justify-content-between w-100">
          <div class="preview-section">
            <small class="text-muted">Preview (Square):</small>
            <div class="preview-container">
              <canvas
                ref="previewCanvas"
                width="80"
                height="80"
                class="preview-canvas"
              ></canvas>
            </div>
          </div>

          <div>
            <b-button variant="secondary" @click="cancel()">
              Cancel
            </b-button>
            <b-button
              variant="primary"
              @click="ok()"
              :disabled="!imageLoaded || processing"
              class="ml-2"
            >
              <b-spinner v-if="processing" small class="mr-1"></b-spinner>
              {{ processing ? "Uploading..." : "Crop & Upload" }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: "ImageCropper",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    imageSrc: {
      type: String,
      required: false,
      default: ""
    },
    orderId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showModal: false,
      processing: false,
      imageLoaded: false,
      image: null,
      canvas: null,
      ctx: null,
      previewCanvas: null,
      previewCtx: null,

      // Image positioning and scaling
      scale: 1,
      offsetX: 0,
      offsetY: 0,

      // Dragging state
      isDragging: false,
      lastX: 0,
      lastY: 0,

      // Canvas dimensions
      canvasWidth: 400,
      canvasHeight: 400,

      // Crop area (always square, centered)
      cropSize: 300
    };
  },
  watch: {
    show(newVal) {
      this.showModal = newVal;
      if (newVal) {
        this.$nextTick(() => {
          this.initializeCropper();
        });
      }
    },
    imageSrc(newVal) {
      // Reload image when imageSrc changes
      if (newVal && this.showModal) {
        this.imageLoaded = false;
        this.loadImage();
      }
    }
  },
  methods: {
    initializeCropper() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext("2d");
      this.previewCanvas = this.$refs.previewCanvas;
      this.previewCtx = this.previewCanvas.getContext("2d");

      // Set canvas size
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;

      // Load image
      this.loadImage();
    },

    loadImage() {
      // Check if imageSrc is valid
      if (!this.imageSrc || this.imageSrc.trim() === "") {
        this.$emit("error", "No image source provided");
        return;
      }

      this.image = new Image();
      this.image.onload = () => {
        // Check if image is large enough for cropping
        if (
          this.image.width < this.cropSize ||
          this.image.height < this.cropSize
        ) {
          this.$emit(
            "error",
            `Image is too small. Minimum size required: ${this.cropSize}x${this.cropSize} pixels. Current size: ${this.image.width}x${this.image.height} pixels.`
          );
          return;
        }

        this.imageLoaded = true;
        this.resetZoom();
        this.drawCanvas();
        this.updatePreview();
      };
      this.image.onerror = () => {
        this.$emit(
          "error",
          "Failed to load image. Please try a different image."
        );
      };
      this.image.src = this.imageSrc;
    },

    getMinimumScale() {
      if (!this.image) return 0.1;

      const minScaleX = this.cropSize / this.image.width;
      const minScaleY = this.cropSize / this.image.height;
      return Math.max(minScaleX, minScaleY);
    },

    constrainImagePosition(offsetX, offsetY) {
      if (!this.image) return { x: offsetX, y: offsetY };

      // Calculate crop area bounds
      const cropX = (this.canvasWidth - this.cropSize) / 2;
      const cropY = (this.canvasHeight - this.cropSize) / 2;
      const cropRight = cropX + this.cropSize;
      const cropBottom = cropY + this.cropSize;

      // Calculate image bounds at current scale
      const imageWidth = this.image.width * this.scale;
      const imageHeight = this.image.height * this.scale;
      const imageRight = offsetX + imageWidth;
      const imageBottom = offsetY + imageHeight;

      // Constrain X position - image must cover crop area horizontally
      let constrainedX = offsetX;
      if (offsetX > cropX) {
        // Image left edge is inside crop area - move left
        constrainedX = cropX;
      } else if (imageRight < cropRight) {
        // Image right edge is inside crop area - move right
        constrainedX = cropRight - imageWidth;
      }

      // Constrain Y position - image must cover crop area vertically
      let constrainedY = offsetY;
      if (offsetY > cropY) {
        // Image top edge is inside crop area - move up
        constrainedY = cropY;
      } else if (imageBottom < cropBottom) {
        // Image bottom edge is inside crop area - move down
        constrainedY = cropBottom - imageHeight;
      }

      return { x: constrainedX, y: constrainedY };
    },

    resetZoom() {
      if (!this.image) return;

      // Calculate minimum scale to ensure image covers the crop area
      const minScale = this.getMinimumScale();

      // Calculate initial scale to fit image in canvas
      const scaleX = this.canvasWidth / this.image.width;
      const scaleY = this.canvasHeight / this.image.height;
      const fitScale = Math.min(scaleX, scaleY) * 0.8; // 80% of fit size

      // Use the larger of minimum scale or fit scale
      this.scale = Math.max(minScale, fitScale);

      // Center the image initially
      const initialOffsetX =
        (this.canvasWidth - this.image.width * this.scale) / 2;
      const initialOffsetY =
        (this.canvasHeight - this.image.height * this.scale) / 2;

      // Apply boundary constraints to ensure crop area is covered
      const constrainedOffset = this.constrainImagePosition(
        initialOffsetX,
        initialOffsetY
      );
      this.offsetX = constrainedOffset.x;
      this.offsetY = constrainedOffset.y;
    },

    drawCanvas() {
      if (!this.ctx || !this.image || !this.imageLoaded) return;

      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Draw image
      this.ctx.drawImage(
        this.image,
        this.offsetX,
        this.offsetY,
        this.image.width * this.scale,
        this.image.height * this.scale
      );

      // Draw crop overlay
      this.drawCropOverlay();
    },

    drawCropOverlay() {
      const cropX = (this.canvasWidth - this.cropSize) / 2;
      const cropY = (this.canvasHeight - this.cropSize) / 2;

      // Draw dark overlay
      this.ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Clear crop area
      this.ctx.clearRect(cropX, cropY, this.cropSize, this.cropSize);

      // Redraw image in crop area only
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect(cropX, cropY, this.cropSize, this.cropSize);
      this.ctx.clip();

      this.ctx.drawImage(
        this.image,
        this.offsetX,
        this.offsetY,
        this.image.width * this.scale,
        this.image.height * this.scale
      );

      this.ctx.restore();

      // Draw crop border
      this.ctx.strokeStyle = "#3399ff";
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(cropX, cropY, this.cropSize, this.cropSize);

      // Draw corner handles
      const handleSize = 8;
      this.ctx.fillStyle = "#3399ff";

      // Top-left
      this.ctx.fillRect(
        cropX - handleSize / 2,
        cropY - handleSize / 2,
        handleSize,
        handleSize
      );
      // Top-right
      this.ctx.fillRect(
        cropX + this.cropSize - handleSize / 2,
        cropY - handleSize / 2,
        handleSize,
        handleSize
      );
      // Bottom-left
      this.ctx.fillRect(
        cropX - handleSize / 2,
        cropY + this.cropSize - handleSize / 2,
        handleSize,
        handleSize
      );
      // Bottom-right
      this.ctx.fillRect(
        cropX + this.cropSize - handleSize / 2,
        cropY + this.cropSize - handleSize / 2,
        handleSize,
        handleSize
      );
    },

    updatePreview() {
      if (!this.previewCtx || !this.image || !this.imageLoaded) return;

      const cropX = (this.canvasWidth - this.cropSize) / 2;
      const cropY = (this.canvasHeight - this.cropSize) / 2;

      // Clear preview canvas
      this.previewCtx.clearRect(0, 0, 80, 80);

      // Calculate the exact area within the crop frame on the original image
      const sourceX = (cropX - this.offsetX) / this.scale;
      const sourceY = (cropY - this.offsetY) / this.scale;
      const sourceWidth = this.cropSize / this.scale;
      const sourceHeight = this.cropSize / this.scale;

      // Ensure source coordinates are within image bounds
      const clampedSourceX = Math.max(
        0,
        Math.min(sourceX, this.image.width - sourceWidth)
      );
      const clampedSourceY = Math.max(
        0,
        Math.min(sourceY, this.image.height - sourceHeight)
      );
      const clampedSourceWidth = Math.min(
        sourceWidth,
        this.image.width - clampedSourceX
      );
      const clampedSourceHeight = Math.min(
        sourceHeight,
        this.image.height - clampedSourceY
      );

      // Draw the exact crop area to preview
      this.previewCtx.drawImage(
        this.image,
        clampedSourceX,
        clampedSourceY,
        clampedSourceWidth,
        clampedSourceHeight,
        0,
        0,
        80,
        80
      );
    },

    startDrag(event) {
      this.isDragging = true;
      const rect = this.canvas.getBoundingClientRect();
      this.lastX = event.clientX - rect.left;
      this.lastY = event.clientY - rect.top;
    },

    drag(event) {
      if (!this.isDragging) return;

      const rect = this.canvas.getBoundingClientRect();
      const currentX = event.clientX - rect.left;
      const currentY = event.clientY - rect.top;

      const deltaX = currentX - this.lastX;
      const deltaY = currentY - this.lastY;

      // Calculate new position
      const newOffsetX = this.offsetX + deltaX;
      const newOffsetY = this.offsetY + deltaY;

      // Apply boundary constraints to keep crop area covered
      const constrainedOffset = this.constrainImagePosition(
        newOffsetX,
        newOffsetY
      );
      this.offsetX = constrainedOffset.x;
      this.offsetY = constrainedOffset.y;

      this.lastX = currentX;
      this.lastY = currentY;

      this.drawCanvas();
      this.updatePreview();
    },

    endDrag() {
      this.isDragging = false;
    },

    handleWheel(event) {
      event.preventDefault();

      if (!this.image) return;

      const delta = event.deltaY > 0 ? -0.1 : 0.1;

      // Apply scale constraints using minimum scale
      const minScale = this.getMinimumScale();
      const newScale = Math.max(minScale, Math.min(5, this.scale + delta));

      if (newScale !== this.scale) {
        // Zoom towards mouse position
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const scaleRatio = newScale / this.scale;
        const newOffsetX = mouseX - (mouseX - this.offsetX) * scaleRatio;
        const newOffsetY = mouseY - (mouseY - this.offsetY) * scaleRatio;

        // Update scale first so constrainImagePosition can use the new scale
        this.scale = newScale;

        // Apply boundary constraints
        const constrainedOffset = this.constrainImagePosition(
          newOffsetX,
          newOffsetY
        );
        this.offsetX = constrainedOffset.x;
        this.offsetY = constrainedOffset.y;

        this.drawCanvas();
        this.updatePreview();
      }
    },

    zoomIn() {
      this.scale = Math.min(5, this.scale + 0.2);

      // Apply boundary constraints after zoom
      const constrainedOffset = this.constrainImagePosition(
        this.offsetX,
        this.offsetY
      );
      this.offsetX = constrainedOffset.x;
      this.offsetY = constrainedOffset.y;

      this.drawCanvas();
      this.updatePreview();
    },

    zoomOut() {
      if (!this.image) return;

      // Don't allow zooming out below minimum scale
      const minScale = this.getMinimumScale();
      this.scale = Math.max(minScale, this.scale - 0.2);

      // Apply boundary constraints after zoom
      const constrainedOffset = this.constrainImagePosition(
        this.offsetX,
        this.offsetY
      );
      this.offsetX = constrainedOffset.x;
      this.offsetY = constrainedOffset.y;

      this.drawCanvas();
      this.updatePreview();
    },

    async cropImage() {
      if (!this.imageLoaded || !this.image) {
        this.$emit("error", "Image not loaded");
        return;
      }

      this.processing = true;

      try {
        // Create a new canvas for the cropped image
        const cropCanvas = document.createElement("canvas");
        const cropCtx = cropCanvas.getContext("2d");

        // Set output size (square)
        const outputSize = 800;
        cropCanvas.width = outputSize;
        cropCanvas.height = outputSize;

        // Calculate source coordinates
        const cropX = (this.canvasWidth - this.cropSize) / 2;
        const cropY = (this.canvasHeight - this.cropSize) / 2;

        // Calculate the exact area within the crop frame on the original image
        const sourceX = (cropX - this.offsetX) / this.scale;
        const sourceY = (cropY - this.offsetY) / this.scale;
        const sourceWidth = this.cropSize / this.scale;
        const sourceHeight = this.cropSize / this.scale;

        // Ensure source coordinates are within image bounds
        const clampedSourceX = Math.max(
          0,
          Math.min(sourceX, this.image.width - sourceWidth)
        );
        const clampedSourceY = Math.max(
          0,
          Math.min(sourceY, this.image.height - sourceHeight)
        );
        const clampedSourceWidth = Math.min(
          sourceWidth,
          this.image.width - clampedSourceX
        );
        const clampedSourceHeight = Math.min(
          sourceHeight,
          this.image.height - clampedSourceY
        );

        // Draw only the exact crop area to the output canvas
        cropCtx.drawImage(
          this.image,
          clampedSourceX,
          clampedSourceY,
          clampedSourceWidth,
          clampedSourceHeight,
          0,
          0,
          outputSize,
          outputSize
        );

        this.$emit("uploading", "Image is being cropped and uploaded");
        // Convert to blob
        const blob = await new Promise((resolve, reject) => {
          cropCanvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to create blob from canvas"));
              }
            },
            "image/jpeg",
            0.9
          );
        });

        // Upload the cropped image
        const uploadResult = await this.uploadCroppedImage(blob);

        this.$emit("cropped", {
          imageUrl: uploadResult.url,
          blob: blob,
          canvas: cropCanvas
        });

        this.showModal = false;
      } catch (error) {
        console.error("Crop and upload failed:", error);
        this.$emit("error", error.message || "Failed to crop and upload image");
      } finally {
        this.processing = false;
      }
    },

    async uploadCroppedImage(blob) {
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      let binaryString = "";
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }

      const headers = {
        headers: {
          "x-api-key": "qIaQZ7nSzt8takE2wP7MY4GxE8NSTaHB4Gj2b3xW"
        }
      };

      const url =
        "https://iys0mdb2wb.execute-api.us-east-2.amazonaws.com/prod/photo/upload";
      const body = {
        id: this.orderId.replace("#", ""),
        filename: `cropped_${Date.now()}.jpg`,
        file: binaryString
      };

      const response = await this.axios.post(
        url,
        JSON.stringify(body),
        headers
      );

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }
    },

    onModalHidden() {
      this.imageLoaded = false;
      this.processing = false;
      this.isDragging = false;
      this.image = null;
      this.$emit("hidden");
    }
  }
};
</script>

<style lang="scss" scoped>
.image-cropper-modal {
  .cropper-container {
    .cropper-wrapper {
      .canvas-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 400px;
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px;

        .crop-canvas {
          cursor: grab;
          border-radius: 4px;

          &:active {
            cursor: grabbing;
          }
        }
      }
    }

    .cropper-controls {
      .zoom-controls {
        .btn {
          font-size: 0.875rem;

          i {
            margin-right: 0.25rem;
          }
        }
      }

      .crop-info {
        font-size: 0.75rem;
      }
    }
  }

  .preview-section {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .preview-container {
      .preview-canvas {
        border: 1px solid #dee2e6;
        border-radius: 4px;
        background: #f8f9fa;
      }
    }
  }
}
</style>
